import React, { createContext, useContext } from "react";
import classNames from "classnames";
import { ReactComponent as RightArrowIcon } from "static/icons/main_right.svg";
import { ReactComponent as LeftArrowIcon } from "static/icons/main_left.svg";
import { ButtonIconWithCustomTouchArea } from "components/Buttons/ButtonIcon";
import styles from "./Table.module.scss";

const TableHeadContext = createContext({ tableHead: false });

interface Props {
    children?: React.ReactNode;
}

export const TableHead = ({ children, ...rest }: Props & JSX.IntrinsicElements["thead"]) => {
    return (
        <TableHeadContext.Provider
            value={{
                tableHead: true,
            }}
        >
            <thead className={styles["table-head"]} {...rest}>
                {children}
            </thead>
        </TableHeadContext.Provider>
    );
};

export const TableBody = ({ children, ...rest }: Props & JSX.IntrinsicElements["tbody"]) => {
    return <tbody {...rest}>{children}</tbody>;
};

const TableHeadCell = ({ children, ...rest }: Props & JSX.IntrinsicElements["th"]) => {
    return (
        <th className={styles["table-head-cell"]} {...rest}>
            {children}
        </th>
    );
};

const TableBodyCell = ({ children, ...rest }: Props & JSX.IntrinsicElements["td"]) => {
    return (
        <td className={styles["table-body-cell"]} {...rest}>
            {children}
        </td>
    );
};

const TableHeadRow = ({ children, ...rest }: Props & JSX.IntrinsicElements["tr"]) => {
    return <tr {...rest}>{children}</tr>;
};

const TableBodyRow = ({ children, ...rest }: Props & JSX.IntrinsicElements["tr"]) => {
    return (
        <tr className={styles["table-body-row"]} {...rest}>
            {children}
        </tr>
    );
};

export const TableRow = ({ children, ...rest }: Props & JSX.IntrinsicElements["tr"]) => {
    const tableHeadContext = useContext(TableHeadContext);
    if (tableHeadContext.tableHead) {
        return <TableHeadRow {...rest}>{children}</TableHeadRow>;
    }
    return <TableBodyRow {...rest}>{children}</TableBodyRow>;
};

export const TableCell = ({
    children,
    ...rest
}: Props & (JSX.IntrinsicElements["th"] | JSX.IntrinsicElements["td"])) => {
    const tableHeadContext = useContext(TableHeadContext);
    if (tableHeadContext.tableHead) {
        return <TableHeadCell {...rest}>{children}</TableHeadCell>;
    }
    return <TableBodyCell {...rest}>{children}</TableBodyCell>;
};

export const Table = ({ children, ...rest }: Props & JSX.IntrinsicElements["table"]) => {
    return (
        <table className={styles["table"]} {...rest}>
            {children}
        </table>
    );
};

interface PaginationButtonsProps {
    next: () => void;
    previous: () => void;
    disableLeftArrow: boolean;
    disableRightArrow: boolean;
    maxCountReq: number;
    startOfPage: number;
    endOfPage: number;
    classes?: {
        root?: string;
    };
}
export const PaginationButtons = ({
    next,
    previous,
    disableLeftArrow,
    disableRightArrow,
    classes,
    maxCountReq,
    endOfPage,
    startOfPage,
}: PaginationButtonsProps) => {
    const LeftArrow = () => {
        return (
            <ButtonIconWithCustomTouchArea
                disabled={disableLeftArrow}
                onClick={previous}
                classes={{
                    root: classNames(styles["arrow"], styles["left-arrow"], {
                        [styles["arrow--disable"]]: disableLeftArrow,
                    }),
                }}
            >
                <LeftArrowIcon />
            </ButtonIconWithCustomTouchArea>
        );
    };
    const RightArrow = () => {
        return (
            <ButtonIconWithCustomTouchArea
                disabled={disableRightArrow}
                onClick={next}
                classes={{
                    root: classNames(styles["arrow"], styles["right-arrow"], {
                        [styles["arrow--disable"]]: disableRightArrow,
                    }),
                }}
            >
                <RightArrowIcon />
            </ButtonIconWithCustomTouchArea>
        );
    };
    return (
        <div className={classNames(styles["pagination"], classes?.root)}>
            <span className={styles["pagination-counter"]}>
                {endOfPage <= maxCountReq
                    ? `${startOfPage}-${endOfPage} of ${maxCountReq}`
                    : `${startOfPage}-${maxCountReq} of ${maxCountReq}`}
            </span>
            <div className={styles["arrow-container"]}>
                <LeftArrow />
                <RightArrow />
            </div>
        </div>
    );
};
